import baseApi from "../../utils/baseApi";
import { User } from "../../types/userTypes";

const USERS_API = "/companyadmin";

class CompanyAdminUserService {
    private static formatPhoneNumber(countryCode: string, phone: string): string {
        return `${countryCode} ${phone}`.trim();
    }

    // ✅ Get all users under the logged-in company admin
    static async getUsersByCompany(): Promise<User[]> {
        try {
            const response = await baseApi.get(`${USERS_API}/userList`);
            return response.data;
        } catch (error: any) {
            console.error("Error fetching users:", error);
            throw new Error(error.response?.data?.message || "Failed to fetch users.");
        }
    }

    // ✅ Add a new user under the company
    static async addUser(userData: Partial<User> & { password: string }): Promise<User> {
        try {
            const response = await baseApi.post(`${USERS_API}/users`, userData);
            return response.data.user;
        } catch (error: any) {
            console.error("Error creating user:", error);
            throw new Error(error.response?.data?.message || "Failed to create user.");
        }
    }

    // ✅ Update an existing user under the company
    static async updateUser(id: number, userData: Partial<User>): Promise<User> {
        try {
            const response = await baseApi.put(`${USERS_API}/users/${id}`, userData);
            return response.data.user;
        } catch (error: any) {
            console.error("Error updating user:", error);
            throw new Error(error.response?.data?.message || "Failed to update user.");
        }
    }

    // ✅ Delete a user under the company
    static async removeUser(id: number): Promise<void> {
        try {
            await baseApi.delete(`${USERS_API}/users/${id}`);
        } catch (error: any) {
            console.error("Error deleting user:", error);
            throw new Error(error.response?.data?.message || "Failed to delete user.");
        }
    }

    // ✅ Assign folder/document access to a user under the company
    static async assignAccess(
        userId: number,
        data: {
            folder_ids?: number[];
            document_ids?: number[];
        }
    ): Promise<void> {
        try {
            await baseApi.post(`${USERS_API}/users/${userId}/assign-access`, data);
        } catch (error: any) {
            console.error("Error assigning access:", error);
            throw new Error(error.response?.data?.message || "Failed to assign access.");
        }
    }

    // ✅ Get assigned folder/document access for a user
    static async getAssignedAccess(userId: number): Promise<{
        folder_ids: number[];
        document_ids: number[];
    }> {
        try {
            const response = await baseApi.get(`${USERS_API}/users/${userId}/assigned-access`);
            return response.data;
        } catch (error: any) {
            console.error("Error getting assigned access:", error);
            throw new Error(error.response?.data?.message || "Failed to fetch assigned access.");
        }
    }

    static async getCompanySpaceContent(): Promise<{
        space_id: number;
        folders: any[];
        documents: any[];
    }> {
        try {
            const response = await baseApi.get(`/companyadmin/space-content`);
            return response.data;
        } catch (error: any) {
            console.error("Error fetching space content:", error);
            throw new Error(error.response?.data?.message || "Failed to fetch space content.");
        }
    }
    
}

export default CompanyAdminUserService;
